import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import useApiService from 'services/api'
import { CompanyPageAccordion } from 'components/NewCompanyPage/CompanyPageAccordion'
import type {
  RecommendationAgent,
  UserRecommendationSettings,
} from 'models/companies'
import { RecommendationNotesEditor } from './RecommendationNotesEditor'

// Mirrors the per-user options on the AI settings page. `turn_off` disables
// the user's daily summary entirely.
const FILTER_LEVELS = [
  { value: 'turn_off', label: 'Turn Off' },
  { value: 'conservative', label: 'Conservative' },
  { value: 'normal', label: 'Normal' },
  { value: 'aggressive', label: 'Aggressive' },
] as const

/**
 * Admin-facing editor for another user's recommendation preferences
 * (FIR-7479). Renders inside the Users-detail edit modal; writes go to the
 * per-user endpoints with ?user_id=<that user> (admin-gated server side).
 * Same controls as the user's own AI settings page: agent toggles, sensitivity,
 * and free-form notes.
 */
export function UserRecommendationPreferences({ userId }: { userId: number }) {
  const api = useApiService()
  const { data, isLoading, isError } = api.useGetRecommendationSettings(userId)

  return (
    <CompanyPageAccordion
      title="Recommendation Preferences"
      defaultOpen={false}
    >
      {isLoading ? (
        <p className="text-sm text-fg-secondary">Loading…</p>
      ) : isError || !data ? (
        <p className="text-sm text-fg-secondary">
          Recommendation settings are unavailable for this user.
        </p>
      ) : (
        <PreferencesEditor key={userId} userId={userId} initial={data} />
      )}
    </CompanyPageAccordion>
  )
}

function PreferencesEditor({
  userId,
  initial,
}: {
  userId: number
  initial: UserRecommendationSettings
}) {
  const api = useApiService()
  const { mutateAsync, isPending } = api.useUpdateRecommendationSettings(userId)

  const [enabledTypes, setEnabledTypes] = useState<string[]>(
    initial.enabled_signal_types
  )
  const [filterLevel, setFilterLevel] = useState(initial.signal_filter_level)

  const agents: RecommendationAgent[] = initial.recommendation_agents ?? []

  const isAgentEnabled = useCallback(
    (agent: RecommendationAgent) =>
      agent.signal_types.some((st) => enabledTypes.includes(st)),
    [enabledTypes]
  )

  const handleToggleAgent = useCallback(
    async (agent: RecommendationAgent) => {
      const currentlyEnabled = agent.signal_types.some((st) =>
        enabledTypes.includes(st)
      )
      const newTypes = currentlyEnabled
        ? enabledTypes.filter((t) => !agent.signal_types.includes(t))
        : Array.from(new Set([...enabledTypes, ...agent.signal_types]))
      const prev = enabledTypes
      setEnabledTypes(newTypes)
      try {
        await mutateAsync({ enabled_signal_types: newTypes })
      } catch {
        toast.error('Failed to update agent setting')
        setEnabledTypes(prev)
      }
    },
    [enabledTypes, mutateAsync]
  )

  const handleFilterLevelChange = useCallback(
    async (value: string) => {
      const prev = filterLevel
      setFilterLevel(value)
      try {
        await mutateAsync({ signal_filter_level: value })
      } catch {
        toast.error('Failed to update sensitivity')
        setFilterLevel(prev)
      }
    },
    [filterLevel, mutateAsync]
  )

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm font-medium text-fg mb-2">Agents</p>
        <div className="flex flex-col gap-2">
          {agents.map((agent) => (
            <label
              key={agent.id}
              className="flex items-center gap-2 text-sm text-fg cursor-pointer"
            >
              <input
                type="checkbox"
                checked={isAgentEnabled(agent)}
                disabled={isPending}
                onChange={() => handleToggleAgent(agent)}
                className="accent-brand"
              />
              {agent.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label
          htmlFor="rec-sensitivity"
          className="block text-sm font-medium text-fg mb-1"
        >
          Sensitivity
        </label>
        <select
          id="rec-sensitivity"
          value={filterLevel}
          disabled={isPending}
          onChange={(e) => handleFilterLevelChange(e.target.value)}
          className="w-full rounded-lg border border-line bg-surface p-2 text-sm text-fg focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        >
          {FILTER_LEVELS.map((level) => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <p className="text-sm font-medium text-fg mb-2">Notes</p>
        <RecommendationNotesEditor userId={userId} />
      </div>
    </div>
  )
}
